import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import PaletteIcon from "@mui/icons-material/Palette";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_post_training_badge,
  API_archive_training_badge,
} from "../../../constants";

import { COLOR_OPTIONS } from "./colorOptions";

const modalStyle = {
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "480px",
  height: "fit-content",
  maxHeight: "92vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  overflow: "auto",
};

function BadgeEditorModal(props) {
  const { open, handleClose, modalDataBadge, homeCompany, onSaveSuccess } = props;
  const isEdit = Boolean(modalDataBadge?.uuid);

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [color, setColor] = React.useState("bg-violet-600");
  const [validityDays, setValidityDays] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [confirmArchive, setConfirmArchive] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName(modalDataBadge?.name || "");
    setDescription(modalDataBadge?.description || "");
    setColor(modalDataBadge?.color || "bg-violet-600");
    setValidityDays(
      modalDataBadge?.validity_days != null ? String(modalDataBadge.validity_days) : ""
    );
    setConfirmArchive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Badge name is required");
      return;
    }
    setIsSaving(true);
    const requestData = {
      company_id: homeCompany,
      training_badge_uuid: modalDataBadge?.uuid || null,
      name: name.trim(),
      description: description,
      color: color,
      validity_days: validityDays === "" ? null : parseInt(validityDays, 10),
    };
    try {
      await postRightworkAPI(API_post_training_badge, requestData);
      toast.success(isEdit ? "Badge updated" : "Badge created");
      onSaveSuccess && onSaveSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to save badge"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    setIsSaving(true);
    try {
      await postRightworkAPI(API_archive_training_badge, {
        company_id: homeCompany,
        training_badge_uuid: modalDataBadge?.uuid,
      });
      toast.success("Badge archived");
      onSaveSuccess && onSaveSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to archive badge"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-lg ${color}`}
              >
                <WorkspacePremiumIcon className="text-white" style={{ fontSize: 20 }} />
              </div>
              <h2 className="text-lg font-bold text-slate-900">
                {isEdit ? "Edit Badge" : "New Badge"}
              </h2>
            </div>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Name
              </label>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Certified Trainer"
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Description
              </label>
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this badge represents (optional)"
                fullWidth
                multiline
                rows={2}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <PaletteIcon className="text-slate-500" style={{ fontSize: "18px" }} />
                <label className="block text-sm font-medium text-slate-700">Color</label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {COLOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setColor(opt.value)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      color === opt.value
                        ? "border-violet-500 bg-violet-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full ${opt.value}`}
                    >
                      {color === opt.value && (
                        <CheckIcon className="text-white" style={{ fontSize: 14 }} />
                      )}
                    </span>
                    <span className="text-slate-700">{opt.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Validity
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Expires</span>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={validityDays}
                  onChange={(e) => setValidityDays(e.target.value)}
                  className="w-24 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-violet-500 focus:outline-none"
                />
                <span className="text-sm text-slate-600">days after earning</span>
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                Blank means the badge never expires. Re-completing the track renews it.
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            {isEdit ? (
              confirmArchive ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">
                    Stops future awards. Earned badges are kept.
                  </span>
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={handleArchive}
                    className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Confirm archive
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmArchive(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-slate-500 hover:text-red-700 rounded-lg text-sm font-medium transition-colors"
                >
                  <ArchiveOutlinedIcon style={{ fontSize: 18 }} />
                  Archive
                </button>
              )
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={handleSave}
                className="px-5 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isEdit ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default BadgeEditorModal;
